import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/i18n/LanguageContext";
import { UsFlag, UsFlagIcon, isUsOrder } from "@/components/UsFlag";
import { HoldStopButton, HoldChip, holdStopDue, canAckHoldStop } from "@/components/HoldStopDialog";
import { StockChip } from "@/components/StockInDialog";

/**
 * Ready to Ship — sub-tab of Packing Orders.
 *
 * Shows every order that has finished the packing station (has a
 * `packed_at` stamp) but has NOT yet been shipped (no `shipping_shipped_at`
 * and/or no tracking number). The list is split into two visual sections:
 *   • 🇺🇸 USA delivery
 *   • 🇨🇦 Canada / Rest of World
 *
 * Purpose: Packing team can see the queue they've handed off to Shipping,
 * grouped by destination so USMCA-eligible boxes are visible at a glance.
 * Read-only — the actual "add tracking + ship" action still lives on the
 * dedicated Shipping Orders tab.
 */
export default function ReadyToShip() {
  const { t } = useT();
  const { user } = useAuth();

  // Fetch every order currently at the Shipping station — this is where
  // packing pushes an order via ✅ Send to Shipping. We filter locally
  // to the "packed but not yet shipped" subset so the tab stays a live
  // hand-off queue.
  const { data = [], isLoading } = useQuery({
    queryKey: ["ready-to-ship"],
    queryFn: async () => (await api.get("/orders", { params: { status: "shipping" } })).data,
    refetchInterval: 30_000,
  });

  const readyRows = React.useMemo(
    () => (data || []).filter(
      (o) => (o.packed_at || o.inventory_packed_at) && !o.shipping_shipped_at
    ),
    [data]
  );
  const usaRows = readyRows.filter(isUsOrder);
  const restRows = readyRows.filter((o) => !isUsOrder(o));

  return (
    <div className="space-y-6" data-testid="ready-to-ship-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{t("Orders")}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("Ready to Ship")}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {readyRows.length} {t("order(s)")} · {t("packed and waiting for a shipping label")}
          </p>
        </div>
      </div>

      <Section
        title={<span className="inline-flex items-center gap-2"><UsFlagIcon className="h-3.5" />{t("USA Delivery")}</span>}
        tone="border-blue-300 bg-blue-50/50 text-blue-900"
        rows={usaRows}
        isLoading={isLoading}
        emptyLabel={t("No USA orders waiting to ship.")}
        testid="rts-usa"
      />

      <Section
        title={t("🇨🇦 Ship to Canada / Rest of World")}
        tone="border-emerald-300 bg-emerald-50/50 text-emerald-900"
        rows={restRows}
        isLoading={isLoading}
        emptyLabel={t("No Canada / rest-of-world orders waiting to ship.")}
        testid="rts-rest"
      />
    </div>
  );
}

/**
 * One coloured section: sticky group header + read-only table of the
 * matching orders. Kept inside this file because it is only used here.
 */
function Section({ title, tone, rows, isLoading, emptyLabel, testid }) {
  const { t } = useT();
  return (
    <section data-testid={testid}>
      <div className={`rounded-t-md border ${tone} px-4 py-2 text-[12px] uppercase tracking-widest font-mono font-semibold flex items-center justify-between`}>
        <span data-testid={`${testid}-title`}>{title}</span>
        <span className="font-mono text-[11px] opacity-80" data-testid={`${testid}-count`}>{rows.length} {t("order(s)")}</span>
      </div>
      <div className="border border-t-0 border-slate-200 rounded-b-md overflow-hidden bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-mono text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">{t("PO #")}</th>
              <th className="text-left px-4 py-2">{t("Customer")}</th>
              <th className="text-left px-4 py-2">{t("Vehicle")}</th>
              <th className="text-left px-4 py-2">{t("Packed On")}</th>
              <th className="text-left px-4 py-2">{t("Packed By")}</th>
              <th className="text-left px-4 py-2">{t("Destination")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{t("Loading…")}</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{emptyLabel}</td></tr>
            )}
            {rows.map((o) => (
              <ReadyRow key={o.id} o={o} testid={testid} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReadyRow({ o, testid }) {
  const { user } = useAuth();
  const packedOn = (o.packed_at || o.inventory_packed_at || "").slice(0, 10) || "—";
  const packedBy = o.packed_by || o.inventory_packed_by || "—";
  const addr = o.shipping_address || {};
  const dest = [addr.city, addr.state, addr.country].filter(Boolean).join(", ") || "—";
  return (
    <tr
      className={o.ship_hold ? "border-t border-red-200 bg-red-50/70 hover:bg-red-50" : "border-t border-slate-100 hover:bg-slate-50"}
      data-testid={`${testid}-row-${o.id}`}
    >
      <td className="px-4 py-3 font-mono font-semibold text-slate-900">
        <Link
          to={`/order/${o.id}`}
          state={{ from: "ready-to-ship" }}
          className="hover:underline inline-flex items-center gap-2"
          data-testid={`${testid}-open-${o.id}`}
        >
          <span>{o.po_number}</span>
          <UsFlag order={o} />
        </Link>
        {o.is_stock_unit && <div className="mt-1"><StockChip o={o} /></div>}
        {o.ship_hold && (
          <div className="mt-1 inline-flex items-center gap-1" data-testid={`${testid}-hold-${o.id}`}>
            <HoldChip o={o} />
            {holdStopDue(o) && canAckHoldStop(user, o.ship_hold_info?.warehouse) && <HoldStopButton orderId={o.id} due stored={!!o.ship_hold_info?.stored} className="h-6 px-2 text-[10px]" />}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-slate-700">{o.customer?.name || "—"}</td>
      <td className="px-4 py-3 text-slate-700 text-xs">
        {[o.brand, o.model, o.year].filter(Boolean).join(" · ") || "—"}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-700">{packedOn}</td>
      <td className="px-4 py-3 text-xs text-slate-600">{packedBy}</td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {dest !== "—" ? (
          <span title={dest}>{dest}</span>
        ) : (
          <Badge className="bg-amber-50 text-amber-800 border border-amber-300 text-[10px]">no address</Badge>
        )}
      </td>
    </tr>
  );
}
